import React from 'react';
import Header from '../components/common/Header';
import Footer from '../components/Footer/Footer';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, HeartPulse, CalendarCheck, ShieldAlert, MessageCircle, Bell, TrendingUp,
  Lock, EyeOff, Eye, Pencil, Trash2, Download, XCircle, SlidersHorizontal, Mail,
} from 'lucide-react';

// Privacy Policy — static, no data/API calls. Same visual system as
// ContactUs.jsx: gradient hero (brand blue -> brand orange accents),
// #EEF3FA page background, white cards with soft shadows.

const C = {
  blue: '#0F4C75',
  blueDeep: '#0B3A5B',
  orange: '#DF8525',
  ink: '#1C2B36',
  body: '#546374',
  muted: '#7C8A9A',
  bg: '#EEF3FA',
  card: '#FFFFFF',
  line: '#E1E9F4',
  tint: '#0F4C750D',
  noteBg: '#FFF4E4',
  noteBorder: '#F3D9AE',
  noteText: '#6B4A12',
};

const s = {
  page: { background: C.bg, paddingBottom: 72 },
  hero: {
    background: `linear-gradient(135deg, ${C.blueDeep} 0%, ${C.blue} 55%, #16628f 100%)`,
    padding: '72px 16px 88px',
    textAlign: 'center',
    color: '#fff',
  },
  eyebrow: {
    display: 'inline-block', padding: '6px 16px', borderRadius: 999,
    background: 'rgba(255,255,255,0.14)', fontSize: 12.5, fontWeight: 700,
    letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 18,
  },
  h1: { margin: '0 0 12px', fontSize: 'clamp(30px, 5vw, 44px)', fontWeight: 800, lineHeight: 1.15 },
  sub: { margin: '0 auto', maxWidth: 620, fontSize: 16.5, lineHeight: 1.7, color: 'rgba(255,255,255,0.88)' },
  breadcrumb: { marginTop: 22, fontSize: 13.5, color: 'rgba(255,255,255,0.75)' },
  breadcrumbLink: { color: '#fff', textDecoration: 'none', fontWeight: 600 },
  wrap: { maxWidth: '90%', margin: '-56px auto 0', padding: '0 16px' },
  card: {
    background: C.card, borderRadius: 18, boxShadow: '0 20px 48px -24px rgba(15, 76, 117, 0.25)',
    border: `1px solid ${C.line}`, padding: '32px 32px', marginBottom: 22,
  },
  sectionHead: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  badge: {
    width: 34, height: 34, borderRadius: 10, background: C.tint, color: C.blue,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
    fontSize: 14, flexShrink: 0,
  },
  h2: { margin: 0, fontSize: 20, fontWeight: 800, color: C.ink },
  h3: { margin: '18px 0 8px', fontSize: 13, fontWeight: 700, color: C.orange, letterSpacing: 0.5, textTransform: 'uppercase' },
  p: { margin: '0 0 12px', fontSize: 15, lineHeight: 1.75, color: C.body },
  ul: { margin: '0 0 4px', paddingLeft: 20 },
  li: { margin: '0 0 6px', fontSize: 14.5, lineHeight: 1.65, color: C.body },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 28px' },
  note: {
    background: C.noteBg, border: `1px solid ${C.noteBorder}`, borderRadius: 12,
    padding: '14px 18px', fontSize: 14, lineHeight: 1.6, color: C.noteText, marginTop: 8,
  },
  featureRow: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 },
  featureIcon: (bg, fg) => ({
    width: 40, height: 40, borderRadius: 12, background: bg, color: fg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  featureTitle: { margin: 0, fontSize: 14.5, fontWeight: 700, color: C.ink },
  featureText: { margin: '2px 0 0', fontSize: 13.5, color: C.muted, lineHeight: 1.55 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 4 },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: C.muted, borderBottom: `2px solid ${C.line}`, textTransform: 'uppercase', letterSpacing: 0.4 },
  td: { padding: '12px 12px', fontSize: 14, color: C.body, borderBottom: `1px solid ${C.line}`, verticalAlign: 'top' },
  tdStrong: { fontWeight: 700, color: C.ink },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginTop: 4 },
  statCard: { border: `1px solid ${C.line}`, borderRadius: 14, padding: '20px 16px', textAlign: 'center' },
  statIcon: { width: 44, height: 44, borderRadius: 12, background: '#0F6E5614', color: '#0F6E56', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' },
  statTitle: { margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: C.ink },
  statText: { margin: 0, fontSize: 12.5, color: C.muted, lineHeight: 1.5 },
  inlineLink: { color: C.blue, fontWeight: 600 },
  footerNote: {
    background: C.card, border: `1px solid ${C.line}`, borderRadius: 18,
    padding: '26px 32px', marginTop: 4,
  },
};

const rightsItems = [
  { icon: Eye, label: 'Access', text: 'View your personal data' },
  { icon: Pencil, label: 'Correction', text: 'Update inaccurate information' },
  { icon: Trash2, label: 'Deletion', text: 'Request account deletion' },
  { icon: Download, label: 'Export', text: 'Download your data' },
  { icon: XCircle, label: 'Withdraw Consent', text: 'Disconnect a wearable or stop sharing at any time' },
  { icon: SlidersHorizontal, label: 'Restriction', text: 'Limit how we use your data' },
];

const useItems = [
  { icon: HeartPulse, title: 'Your Health Insights', text: 'To show your health trends, reports and family-planning score' },
  { icon: CalendarCheck, title: 'Appointments', text: 'To book consultations and show times in your own time zone' },
  { icon: ShieldAlert, title: 'Security', text: 'To verify your account and protect against fraud and abuse' },
  { icon: MessageCircle, title: 'Support', text: 'To answer your questions and requests' },
  { icon: Bell, title: 'Reminders and Alerts', text: 'To send reminders, one-time passcodes and service messages' },
  { icon: TrendingUp, title: 'Improvement', text: 'To make the service more reliable and useful' },
];

const subscriptionUses = [
  'Activate and manage your subscription.',
  'Provide access to the features included in your selected plan.',
  'Process payments, renewals, cancellations, and eligible refunds.',
  'Respond to billing inquiries and resolve payment disputes.',
  'Maintain financial records and comply with legal obligations.',
];

const shareRows = [
  { scenario: 'Doctors You Book', info: 'Contact details, appointment history, and the health and report information needed for your care. Only doctors with a confirmed or completed appointment with you can view it.' },
  { scenario: 'Service Providers', info: 'Data needed for hosting, SMS and email delivery, and video meetings (for example the meeting time and topic for a Zoom appointment)' },
  { scenario: 'Payment Providers', info: 'Information needed to process subscription payments, renewals, cancellations and refunds, handled under the provider\'s own privacy and security practices' },
  { scenario: 'Legal Requirements', info: "When required by law or to protect someone's safety" },
  { scenario: 'Business Transfers', info: 'In case of merger, acquisition, or sale' },
];

const securityCards = [
  { icon: Lock, title: 'Encryption', text: 'Data is encrypted in transit, and passwords are stored hashed' },
  { icon: ShieldCheck, title: 'Access Controls', text: 'Health data is limited to you and the doctors you have booked' },
  { icon: EyeOff, title: 'Anonymization', text: 'Data used for analytics is anonymized' },
];

function SectionCard({ index, title, children }) {
  return (
    <div style={s.card}>
      <div style={s.sectionHead}>
        <span style={s.badge}>{index}</span>
        <h2 style={s.h2}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

const PrivacyPolicy = () => {
  return (
    <>
      <Header />
      <div style={s.page}>
        <div style={s.hero}>
          <span style={s.eyebrow}>Your Privacy</span>
          <h1 style={s.h1}>Privacy Policy</h1>
          <p style={s.sub}>
            Your health and family-planning information is personal. Here's what we collect, how we use it, and
            the choices you have.
          </p>
          <div style={s.breadcrumb}>
            <Link to="/" style={s.breadcrumbLink}>Home</Link>
            <span style={{ margin: '0 8px' }}>/</span>
            <span>Privacy Policy</span>
          </div>
        </div>

        <div style={s.wrap}>
          <SectionCard index={1} title="Introduction">
            <p style={s.p}>
              Future Family is a family-planning and fertility support platform. It helps you track your health,
              understand genetic and family-planning information, and book consultations with doctors and
              counselors. This Privacy Policy explains what personal information we collect, how we use it, who
              can see it, and the choices you have.
            </p>
            <p style={{ ...s.p, marginBottom: 0 }}>
              By creating an account or using Future Family, you consent to the practices described in this
              policy. If you do not agree, please do not access or use our services.
            </p>
          </SectionCard>

          <SectionCard index={2} title="Information We Collect">
            <div style={s.grid2}>
              <div>
                <p style={s.h3}>Personal Information</p>
                <ul style={s.ul}>
                  <li style={s.li}>Name, email address and password</li>
                  <li style={s.li}>Phone number with country code</li>
                  <li style={s.li}>Date of birth</li>
                  <li style={s.li}>Country and time zone</li>
                  <li style={s.li}>Family-planning goals</li>
                  <li style={s.li}>Profile and health background</li>
                  <li style={s.li}>Appointment history and doctor notes</li>
                  <li style={s.li}>Support messages</li>
                  <li style={s.li}>Subscription and billing records (see section 4)</li>
                </ul>
              </div>
              <div>
                <p style={s.h3}>Automatically Collected</p>
                <ul style={s.ul}>
                  <li style={s.li}>Device information and IP address</li>
                  <li style={s.li}>Browser type and operating system</li>
                  <li style={s.li}>Usage and interaction data</li>
                  <li style={s.li}>Essential cookies and local storage</li>
                </ul>
              </div>
            </div>

            <p style={s.h3}>Health and Genetic Information</p>
            <div style={s.grid2}>
              <ul style={s.ul}>
                <li style={s.li}>Wearable readings: heart rate, heart-rate variability, blood pressure</li>
                <li style={s.li}>Respiratory rate, skin temperature, movement and active energy</li>
              </ul>
              <ul style={s.ul}>
                <li style={s.li}>Genetic test results and reports you upload</li>
                <li style={s.li}>Medical documents and files you choose to share</li>
              </ul>
            </div>

            <div style={s.note}>
              <strong>Note:</strong> Health and genetic information is sensitive. We collect it only when you
              connect a device or upload it yourself, and you stay in control of what you share.
            </div>
          </SectionCard>

          <SectionCard index={3} title="How We Use Your Information">
            <div style={s.grid2}>
              {useItems.map((it) => {
                const Icon = it.icon;
                return (
                  <div style={s.featureRow} key={it.title}>
                    <div style={s.featureIcon('#0F4C7514', C.blue)}><Icon size={18} /></div>
                    <div>
                      <p style={s.featureTitle}>{it.title}</p>
                      <p style={s.featureText}>{it.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard index={4} title="Subscription and Billing Information">
            <p style={s.p}>
              When you subscribe to a Future Family plan, we may process information necessary to manage your
              subscription, provide access to paid features, and support billing-related requests.
            </p>

            <p style={s.h3}>Information We May Process</p>
            <p style={s.p}>
              Depending on your subscription and payment method, this information may include your subscription
              plan, billing frequency, subscription status, transaction records, renewal dates, cancellation
              status, and payment-related identifiers.
            </p>

            <p style={s.h3}>Payment Processing</p>
            <p style={s.p}>
              Payments may be processed through our designated payment providers or supported third-party
              platforms. Payment information submitted directly to a payment provider is handled according to
              that provider's privacy and security practices. We process or receive only the payment-related
              information necessary for our legitimate operational purposes, subject to applicable law.
            </p>

            <p style={s.h3}>How We Use Subscription Information</p>
            <p style={s.p}>We may use subscription information to:</p>
            <ul style={s.ul}>
              {subscriptionUses.map((t) => <li style={s.li} key={t}>{t}</li>)}
            </ul>

            <p style={s.h3}>Subscription Management and Third Parties</p>
            <p style={s.p}>
              If you purchase or manage a subscription through a third-party platform, certain subscription and
              transaction information may be processed by that platform under its own terms and privacy policy.
            </p>

            <p style={s.h3}>Your Choices</p>
            <p style={s.p}>
              You can review available subscription options through our{' '}
              <Link to="/upgrade-profile" style={s.inlineLink}>Upgrade Profile page</Link>. Your
              subscription-management and cancellation options will depend on how you purchased your plan.
            </p>

            <p style={{ ...s.p, marginBottom: 0 }}>
              For additional information about how Future Family collects, uses, protects, and retains personal
              information, please review the remainder of this Privacy Policy.
            </p>
          </SectionCard>

          <SectionCard index={5} title="Information Sharing">
            <p style={s.p}>We may share your information in the following circumstances:</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Scenario</th>
                    <th style={s.th}>Information Shared</th>
                  </tr>
                </thead>
                <tbody>
                  {shareRows.map((r) => (
                    <tr key={r.scenario}>
                      <td style={{ ...s.td, ...s.tdStrong }}>{r.scenario}</td>
                      <td style={s.td}>{r.info}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={s.note}>
              <strong>We do NOT sell your personal information</strong>, and we never use your health or genetic
              data for advertising.
            </div>
          </SectionCard>

          <SectionCard index={6} title="Data Security">
            <p style={s.p}>
              We use appropriate technical and organizational safeguards to protect your personal information,
              including:
            </p>
            <div style={s.statGrid}>
              {securityCards.map((c) => {
                const Icon = c.icon;
                return (
                  <div style={s.statCard} key={c.title}>
                    <div style={s.statIcon}><Icon size={20} /></div>
                    <p style={s.statTitle}>{c.title}</p>
                    <p style={s.statText}>{c.text}</p>
                  </div>
                );
              })}
            </div>
            <p style={{ ...s.p, marginTop: 16, marginBottom: 0 }}>
              No online service can be completely secure. Please use a strong, unique password and tell us right
              away if you think someone else has accessed your account.
            </p>
          </SectionCard>

          <SectionCard index={7} title="Your Rights">
            <p style={s.p}>You have the following rights regarding your personal data:</p>
            <div style={s.grid2}>
              {rightsItems.map((it) => {
                const Icon = it.icon;
                return (
                  <div style={s.featureRow} key={it.label}>
                    <div style={s.featureIcon('#DF852514', C.orange)}><Icon size={17} /></div>
                    <div>
                      <p style={s.featureTitle}>{it.label}</p>
                      <p style={s.featureText}>{it.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p style={{ margin: 0, fontSize: 14.5, color: C.body, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={15} color={C.blue} />
              To exercise these rights, contact us at{' '}
              <a href="mailto:info@futurefamilyai.com" style={{ color: C.blue, fontWeight: 700, textDecoration: 'none' }}>
                info@futurefamilyai.com
              </a>
            </p>
          </SectionCard>

          <SectionCard index={8} title="Data Retention">
            <p style={s.p}>We keep your personal data only as long as necessary for:</p>
            <ul style={s.ul}>
              <li style={s.li}>Providing our services to you</li>
              <li style={s.li}>Complying with legal and medical record-keeping obligations</li>
              <li style={s.li}>Maintaining financial and transaction records</li>
              <li style={s.li}>Resolving disputes and enforcing agreements</li>
              <li style={s.li}>Keeping the service secure</li>
            </ul>
            <p style={{ ...s.p, marginTop: 8, marginBottom: 0 }}>
              When you delete your account, we will remove your personal data within 30 days, except where the
              law requires us to keep it.
            </p>
          </SectionCard>

          <SectionCard index={9} title="Children and International Use">
            <p style={s.p}>
              Future Family is for adults. We do not knowingly collect information from anyone under 18. If you
              believe a child has given us personal information, contact us and we will delete it.
            </p>
            <p style={{ ...s.p, marginBottom: 0 }}>
              Future Family is used in many countries, so your information may be processed on servers outside
              your own. When we transfer data across borders, we take steps to keep it protected in line with
              this policy and applicable law.
            </p>
          </SectionCard>

          <div style={s.footerNote}>
            <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 800, color: C.ink }}>Policy Updates</p>
            <p style={{ margin: 0, fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
              We may update this Privacy Policy from time to time. We will notify you of significant changes in
              the app or by email, and by posting the new policy on this page and updating the "Last Updated"
              date.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;